import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { BarChart2, BarChart3, Users, Calendar, Activity, LogOut, UserPlus, Menu, X, CalendarCheck, CheckCircle2, Briefcase, TrendingUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from './AuthProvider';
import { useLanguage } from '../contexts/LanguageContext';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/auth';
import LanguageToggle from './ui/LanguageToggle';
import { getAccessibleClientIds } from '../lib/multiClientUtils';

const Layout: React.FC = () => {
  const location = useLocation();
  const { profile, signOut } = useAuth();
  const { t } = useLanguage();
  const [clientName, setClientName] = useState<string>('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userClientCount, setUserClientCount] = useState<number>(0);

  useEffect(() => {
    const fetchClientData = async () => {
      if (profile?.client_id) {
        const { data, error } = await supabase
          .from('clients')
          .select('name')
          .eq('id', profile.client_id)
          .maybeSingle();

        if (!error && data) {
          setClientName(data.name);
        }
      }

      if (profile?.user_id && profile?.role) {
        const clientIds = await getAccessibleClientIds(profile.user_id, profile.role);
        setUserClientCount(clientIds.length);
      }
    };

    fetchClientData();
  }, [profile?.client_id, profile?.user_id, profile?.role]);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleSignOut = async () => {
    await signOut();
  };

  const isClient = profile?.role === 'client';
  const hasMultipleClients = userClientCount > 1;

  const navigationItems = [
    {
      path: '/',
      icon: BarChart2,
      label: t('nav.dashboard'),
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-50',
      textColor: 'text-pink-700',
      show: true
    },
    {
      path: '/statistiques',
      icon: BarChart3,
      label: 'Statistiques',
      color: 'from-rose-500 to-pink-500',
      bgColor: 'bg-rose-50',
      textColor: 'text-rose-700',
      show: true
    },
    {
      path: '/appointments',
      icon: Calendar,
      label: t('nav.appointments'),
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
      show: true
    },
    {
      path: '/prospecting',
      icon: Activity,
      label: t('nav.prospecting'),
      color: 'from-purple-500 to-violet-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700',
      show: true
    },
    {
      path: '/campagnes',
      icon: Briefcase,
      label: 'Campagnes',
      color: 'from-amber-500 to-yellow-500',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
      show: profile?.role === 'super_admin'
    },
    {
      path: '/confirmation-rdvs',
      icon: CheckCircle2,
      label: 'Day to Day',
      color: 'from-teal-500 to-cyan-500',
      bgColor: 'bg-teal-50',
      textColor: 'text-teal-700',
      show: profile?.role === 'admin' || profile?.role === 'super_admin' || profile?.role === 'sdr'
    },
    {
      path: '/agenda-planning',
      icon: CalendarCheck,
      label: 'Planning',
      color: 'from-sky-500 to-blue-500',
      bgColor: 'bg-sky-50',
      textColor: 'text-sky-700',
      show: profile?.role === 'admin' || profile?.role === 'super_admin' || profile?.role === 'sdr'
    },
    {
      path: '/clients',
      icon: Users,
      label: t('nav.clients'),
      color: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      show: profile?.role === 'admin' || profile?.role === 'super_admin' || profile?.role === 'sdr'
    },
    {
      path: '/users',
      icon: UserPlus,
      label: t('nav.users'),
      color: 'from-orange-500 to-amber-500',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-700',
      show: profile?.role === 'admin' || profile?.role === 'super_admin' || profile?.role === 'sdr'
    },
    {
      path: '/stats-manager',
      icon: TrendingUp,
      label: 'Stats Manager',
      color: 'from-violet-500 to-fuchsia-500',
      bgColor: 'bg-violet-50',
      textColor: 'text-violet-700',
      show: profile?.role === 'super_admin'
    }
  ];

  const NavItem = ({ item, isMobile = false }: { item: any; isMobile?: boolean }) => {
    const active = isActive(item.path);
    
    return (
      <Link 
        to={item.path} 
        className={`group relative flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${
          active 
            ? `bg-gradient-to-r ${item.color} text-white shadow-lg transform scale-105` 
            : 'text-gray-700 hover:bg-gray-50'
        }`}
        onClick={isMobile ? () => setIsMobileMenuOpen(false) : undefined}
      >
        {/* Active indicator */}
        {active && (
          <motion.div
            layoutId="activeIndicator"
            className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r-full"
            initial={false}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          />
        )}
        
        {/* Icon with background */}
        <div className={`relative p-2 rounded-lg mr-3 transition-all duration-200 ${
          active 
            ? 'bg-white/20' 
            : `${item.bgColor} group-hover:scale-110`
        }`}>
          <item.icon className={`h-5 w-5 transition-colors duration-200 ${
            active ? 'text-white' : item.textColor
          }`} />
        </div>
        
        {/* Label */}
        <span className={`font-medium transition-colors duration-200 ${
          active ? 'text-white' : 'text-gray-700 group-hover:text-gray-900'
        }`}>
          {item.label}
        </span>

        {/* Hover effect */}
        {!active && (
          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-gray-100 to-gray-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 -z-10" />
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Desktop Sidebar */}
      <div className="hidden md:fixed md:inset-y-0 md:left-0 md:z-50 md:w-64 md:flex md:flex-col">
        <div className="flex flex-col h-full bg-white shadow-xl border-r border-gray-200">
          {/* Header */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <img 
              src="https://framerusercontent.com/images/Ol7Om4r0GvGTsCb7yaXJVXSeA.png" 
              alt="Hook Lab" 
              className="h-8 w-auto"
            />
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-xs text-gray-500 font-medium">Online</span>
            </div>
          </div>

          {/* User Info */}
          <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-sm">
                  {(clientName || profile?.full_name || 'U').charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {clientName || profile?.full_name || 'User'}
                </p>
                <p className="text-xs text-gray-500 capitalize">
                  {profile?.role || 'User'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigationItems.filter(item => item.show).map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </nav>
          
          {/* Language Toggle & Sign Out */}
          <div className="p-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white space-y-3">
            <div className="flex justify-center bg-gray-100 rounded-lg p-1">
              <LanguageToggle />
            </div>
            <button
              onClick={handleSignOut}
              className="flex items-center w-full px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all duration-200 group"
            >
              <div className="p-2 rounded-lg mr-3 bg-gray-100 group-hover:bg-red-100 transition-colors duration-200">
                <LogOut className="h-5 w-5 text-gray-600 group-hover:text-red-600 transition-colors duration-200" />
              </div>
              <span className="font-medium">{t('nav.signOut')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white shadow-lg z-50 border-b border-gray-200">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <img 
              src="https://framerusercontent.com/images/Ol7Om4r0GvGTsCb7yaXJVXSeA.png" 
              alt="Hook Lab" 
              className="h-6 w-auto"
            />
          </div>
          <div className="flex items-center space-x-3">
            <LanguageToggle />
            <div className="w-8 h-8 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-xs">
                {(clientName || profile?.full_name || 'U').charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 z-40 bg-black bg-opacity-50" 
            onClick={() => setIsMobileMenuOpen(false)} 
          />
        )}
      </AnimatePresence>

      {/* Mobile sidebar */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="md:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl"
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <img 
                  src="https://framerusercontent.com/images/Ol7Om4r0GvGTsCb7yaXJVXSeA.png" 
                  alt="Hook Lab" 
                  className="h-8 w-auto"
                />
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* User Info */}
              <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {(clientName || profile?.full_name || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {clientName || profile?.full_name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">
                      {profile?.role || 'User'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Navigation */}
              <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
                {navigationItems.filter(item => item.show).map((item) => (
                  <NavItem key={item.path} item={item} isMobile />
                ))}
              </nav>
              
              {/* Sign Out */}
              <div className="p-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <button
                  onClick={() => {
                    handleSignOut();
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all duration-200 group"
                >
                  <div className="p-2 rounded-lg mr-3 bg-gray-100 group-hover:bg-red-100 transition-colors duration-200">
                    <LogOut className="h-5 w-5 text-gray-600 group-hover:text-red-600 transition-colors duration-200" />
                  </div>
                  <span className="font-medium">{t('nav.signOut')}</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="md:ml-64">
        <main className="pt-16 md:pt-0 min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;